import {
  type BreakpointInfo,
  type CoreSnapshot,
  type DebugRuleAction,
  type DebuggerRuleInfo,
  type DebuggerRuleSpec,
  type Emulator,
  type TraceLogEntry
} from "./emulator.js";
import type { RunloopMetrics } from "./runloop.js";
import {
  type MachineSnapshots,
  type MachineStateTab,
  type MachineStateTabId,
  formatHexWord,
  isPlainAddressBreakpoint,
  renderMachineState,
  renderSnapshot,
  setMachineStateTabState,
  stopReasonText
} from "./debugger-machine-state.js";
import {
  clearDisassembly,
  deriveDisassemblyBase,
  renderDisassembly as renderDisassemblyWindow
} from "./debugger-disassembly.js";

export type DebuggerActions = {
  activate(): void;
  continue(): void;
  pause(): void;
  step(count: number): void;
};

export type DebuggerRuntime = Pick<
  RunloopMetrics,
  "cycleCount" | "debuggerActive" | "frameNo" | "state" | "stopReason"
>;

type MemoryPatchMode = "bytes" | "fill";

export type DebuggerElements = {
  pauseTrigger: HTMLButtonElement;
  continueTrigger: HTMLButtonElement;
  stepTrigger: HTMLButtonElement;
  step10Trigger: HTMLButtonElement;
  refreshTrigger: HTMLButtonElement;
  stopReason: HTMLParagraphElement;
  runtimeState: HTMLParagraphElement;
  machineTabCpu: HTMLButtonElement;
  machineTabIoc: HTMLButtonElement;
  machineTabVoc: HTMLButtonElement;
  machineTabAoc: HTMLButtonElement;
  machineTabTic: HTMLButtonElement;
  machineTabDic: HTMLButtonElement;
  machinePanelCpu: HTMLElement;
  machinePanelIoc: HTMLElement;
  machinePanelVoc: HTMLElement;
  machinePanelAoc: HTMLElement;
  machinePanelTic: HTMLElement;
  machinePanelDic: HTMLElement;
  iocClearTrigger: HTMLButtonElement;
  registerPc: HTMLElement;
  registerA: HTMLElement;
  registerX: HTMLElement;
  registerY: HTMLElement;
  registerSp: HTMLElement;
  registerP: HTMLElement;
  registerIrq: HTMLElement;
  registerNmi: HTMLElement;
  phaseValue: HTMLElement;
  flagN: HTMLElement;
  flagV: HTMLElement;
  flagI: HTMLElement;
  flagZ: HTMLElement;
  flagC: HTMLElement;
  flagFetch: HTMLElement;
  flagHalt: HTMLElement;
  breakpointAddress: HTMLInputElement;
  ruleModeBreak: HTMLInputElement;
  ruleModeTrace: HTMLInputElement;
  ruleModeTraceStop: HTMLInputElement;
  traceFieldsRow: HTMLElement;
  traceFields: HTMLInputElement;
  traceLimitsRow: HTMLElement;
  traceEveryNth: HTMLInputElement;
  traceMaxEvents: HTMLInputElement;
  ruleNameRow: HTMLElement;
  ruleName: HTMLInputElement;
  addBreakpointTrigger: HTMLButtonElement;
  showBreakpointTrigger: HTMLButtonElement;
  clearBreakpointsTrigger: HTMLButtonElement;
  breakpointsOutput: HTMLElement;
  patchAddress: HTMLInputElement;
  patchReadLength: HTMLInputElement;
  patchModeBytes: HTMLInputElement;
  patchModeFill: HTMLInputElement;
  patchCurrent: HTMLElement;
  patchValueLabel: HTMLLabelElement;
  patchValue: HTMLInputElement;
  patchCountRow: HTMLElement;
  patchCount: HTMLInputElement;
  patchReadTrigger: HTMLButtonElement;
  patchApplyTrigger: HTMLButtonElement;
  patchWarning: HTMLParagraphElement;
  patchStatus: HTMLParagraphElement;
  traceClearTrigger: HTMLButtonElement;
  traceToggleTrigger: HTMLButtonElement;
  traceExportTrigger: HTMLButtonElement;
  traceAutoscrollToggle: HTMLInputElement;
  traceOutput: HTMLElement;
  disassemblyOutput: HTMLPreElement;
  followPcToggle: HTMLInputElement;
  iocRegisters: HTMLElement;
  iocInternal: HTMLElement;
  iocDerived: HTMLElement;
  vocRegisters: HTMLElement;
  vocInternal: HTMLElement;
  vocDerived: HTMLElement;
  aocRegisters: HTMLElement;
  aocInternal: HTMLElement;
  aocDerived: HTMLElement;
  ticRegisters: HTMLElement;
  ticInternal: HTMLElement;
  ticDerived: HTMLElement;
  dicRegisters: HTMLElement;
  dicInternal: HTMLElement;
  dicDerived: HTMLElement;
};

export type DebuggerController = {
  refresh(): void;
  setActions(actions: DebuggerActions): void;
  updateRuntime(runtime: DebuggerRuntime): void;
};

function asBreakpointInfo(rule: DebuggerRuleInfo): BreakpointInfo {
  return {
    text: rule.matchText,
    anchorAddress: rule.anchorAddress
  };
}

function ruleBadgeText(action: DebugRuleAction): string {
  switch (action) {
    case "break":
      return "break";
    case "trace":
      return "trace";
    case "trace_stop":
      return "trace+stop";
  }
  return "break";
}

function ruleNoticeText(rule: DebuggerRuleInfo): string {
  switch (rule.action) {
    case "break":
      return rule.anchorAddress !== null && isPlainAddressBreakpoint(rule.matchText)
        ? `Added breakpoint 0x${formatHexWord(rule.anchorAddress)}.`
        : `Added breakpoint: ${rule.matchText}.`;
    case "trace":
      return `Added trace rule: ${rule.name}.`;
    case "trace_stop":
      return `Added trace+stop rule: ${rule.name}.`;
  }
  return `Added breakpoint: ${rule.matchText}.`;
}

function parsePositiveField(raw: string, label: string): number {
  const value = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
    throw new Error(`${label} must be >= 1`);
  }
  return value;
}

function parseDebuggerNumber(raw: string, label: string): number {
  const text = raw.trim();
  let value: number;

  if (text === "") {
    throw new Error(`${label} is required`);
  }
  if (text.startsWith("$")) {
    value = Number.parseInt(text.slice(1), 16);
  } else {
    value = Number(text);
  }
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`${label} must be a finite integer`);
  }
  return value;
}

function parseHexByte(raw: string, label: string): number {
  const text = raw.trim();
  const normalized = text.startsWith("$") ? text.slice(1) : text.replace(/^0x/i, "");

  if (!/^[0-9a-f]{1,2}$/i.test(normalized)) {
    throw new Error(`${label} must be a hex byte`);
  }

  return Number.parseInt(normalized, 16);
}

function parseByteSequence(raw: string): Uint8Array {
  const text = raw.trim();
  const tokens: string[] = [];

  if (text === "") {
    throw new Error("Patch bytes are required");
  }

  if (/[\s,]/.test(text)) {
    tokens.push(
      ...text
        .split(/[\s,]+/)
        .map((token) => token.trim())
        .filter((token) => token !== "")
    );
  } else {
    const normalized = text.startsWith("$") ? text.slice(1) : text.replace(/^0x/i, "");

    if (!/^[0-9a-f]+$/i.test(normalized)) {
      throw new Error("Patch bytes must be hex values");
    }
    if (normalized.length <= 2) {
      tokens.push(normalized);
    } else {
      if ((normalized.length & 1) !== 0) {
        throw new Error("Compact patch bytes must use full hex pairs");
      }
      for (let index = 0; index < normalized.length; index += 2) {
        tokens.push(normalized.slice(index, index + 2));
      }
    }
  }

  if (tokens.length === 0) {
    throw new Error("Patch bytes are required");
  }

  return Uint8Array.from(tokens.map((token, index) => parseHexByte(token, `Patch byte ${index + 1}`)));
}

function formatPatchBytes(bytes: Uint8Array): string {
  if (bytes.length === 0) {
    return "No preview yet.";
  }

  const lines: string[] = [];
  for (let offset = 0; offset < bytes.length; offset += 8) {
    const slice = bytes.slice(offset, offset + 8);
    lines.push(Array.from(slice, (value) => value.toString(16).toUpperCase().padStart(2, "0")).join(" "));
  }
  return lines.join("\n");
}

function overlapsRange(start: number, length: number, rangeStart: number, rangeEnd: number): boolean {
  const end = start + length - 1;
  return start <= rangeEnd && end >= rangeStart;
}

function buildPatchWarning(address: number, length: number): string {
  const warnings: string[] = [];

  if (length <= 0) {
    return "";
  }
  if (overlapsRange(address, length, 0xd000, 0xdfff)) {
    warnings.push("Writes in 0xD000..0xDFFF can trigger live MMIO side effects.");
  }
  if (overlapsRange(address, length, 0xa000, 0xbfff) || overlapsRange(address, length, 0xe000, 0xffff)) {
    warnings.push("Writes in banked ROM windows may not behave like plain RAM.");
  }
  return warnings.join(" ");
}

function resolvePatchReadLength(raw: string, address: number, memorySize: number): number {
  const count = parsePositiveField(raw, "Read count");

  if (count > memorySize) {
    throw new Error(`Read count must be <= ${memorySize}.`);
  }
  if (address + count > memorySize) {
    throw new Error("Read range exceeds address space.");
  }
  return count;
}

export function setupDebugger(emulator: Emulator, elements: DebuggerElements): DebuggerController {
  let actions: DebuggerActions | null = null;
  let runtime: DebuggerRuntime = {
    cycleCount: emulator.getCycleCount().toString(),
    debuggerActive: false,
    frameNo: emulator.getFrameNo(),
    state: "paused_manual",
    stopReason: "none"
  };
  let lastSnapshot: CoreSnapshot | null = null;
  let lastRule: DebuggerRuleInfo | null = null;
  let lastNotice: string | null = null;
  let followPc = true;
  let focusedAddress: number | null = null;
  let manualBase: number | null = null;
  let activeMachineTab: MachineStateTabId = "cpu";
  let lastTraceEntries: TraceLogEntry[] = [];
  let patchPreviewAddress: number | null = null;
  let patchPreviewLength = 8;
  let patchPreviewText = "No preview yet.";
  let patchStatusText = "Nothing yet.";
  const runCard = elements.pauseTrigger.closest(".tool-card") as HTMLElement | null;
  const machineCard = elements.machinePanelCpu.closest(".tool-card") as HTMLElement | null;
  const rulesCard = elements.breakpointAddress.closest(".tool-card") as HTMLElement | null;
  const patchCard = elements.patchAddress.closest(".tool-card") as HTMLElement | null;
  const codeCard = elements.disassemblyOutput.closest(".tool-card") as HTMLElement | null;
  const machineTabs: MachineStateTab[] = [
    { id: "cpu", button: elements.machineTabCpu, panel: elements.machinePanelCpu },
    { id: "ioc", button: elements.machineTabIoc, panel: elements.machinePanelIoc },
    { id: "voc", button: elements.machineTabVoc, panel: elements.machinePanelVoc },
    { id: "aoc", button: elements.machineTabAoc, panel: elements.machinePanelAoc },
    { id: "tic", button: elements.machineTabTic, panel: elements.machinePanelTic },
    { id: "dic", button: elements.machineTabDic, panel: elements.machinePanelDic }
  ];

  const currentPatchMode = (): MemoryPatchMode => {
    if (elements.patchModeFill.checked) {
      return "fill";
    }
    return "bytes";
  };

  const parsePatchAddress = (): number => {
    const address = parseDebuggerNumber(elements.patchAddress.value, "Patch address");
    if (address < 0 || address >= emulator.getMemorySize()) {
      throw new Error(`Patch address must be in range 0x0000..0x${formatHexWord(emulator.getMemorySize() - 1)}.`);
    }
    return address;
  };

  const parsePatchReadLength = (address: number): number => {
    return resolvePatchReadLength(elements.patchReadLength.value, address, emulator.getMemorySize());
  };

  const patchReadLengthOrDefault = (address: number): number => {
    try {
      return parsePatchReadLength(address);
    } catch {
      return Math.min(8, emulator.getMemorySize() - address);
    }
  };

  const buildPatchPayload = (): Uint8Array => {
    const mode = currentPatchMode();

    switch (mode) {
      case "bytes":
        return parseByteSequence(elements.patchValue.value);
      case "fill": {
        const fillByte = parseHexByte(elements.patchValue.value, "Fill byte");
        return new Uint8Array(parsePositiveField(elements.patchCount.value, "Repeat count")).fill(fillByte);
      }
    }
  };

  const renderPatchPreview = (): void => {
    elements.patchCurrent.textContent = patchPreviewText;
    elements.patchStatus.textContent = patchStatusText;
  };

  const syncDesktopCardHeights = (): void => {
    if (runCard === null || machineCard === null || rulesCard === null || patchCard === null || codeCard === null) {
      return;
    }
    runCard.style.minHeight = "";
    codeCard.style.height = "";
    if (!window.matchMedia("(min-width: 901px)").matches) {
      return;
    }

    const machineHeight = machineCard.getBoundingClientRect().height;
    const rulesHeight = rulesCard.getBoundingClientRect().height;
    const patchHeight = patchCard.getBoundingClientRect().height;
    const sidebarHeight = rulesHeight + 16 + patchHeight;

    runCard.style.minHeight = `${Math.ceil(machineHeight)}px`;
    codeCard.style.height = `${Math.ceil(sidebarHeight)}px`;
  };

  const renderPatchWarning = (): void => {
    try {
      const payloadLength = Math.max(buildPatchPayload().length, 1);
      elements.patchWarning.textContent = buildPatchWarning(parsePatchAddress(), payloadLength);
    } catch {
      elements.patchWarning.textContent = "";
    }
  };

  const refreshPatchPreview = (): void => {
    if (patchPreviewAddress === null) {
      renderPatchPreview();
      return;
    }
    const preview = emulator.copyMemory(patchPreviewAddress, patchPreviewLength);
    patchPreviewText = formatPatchBytes(preview);
    renderPatchPreview();
  };

  const focusAddress = (address: number): void => {
    followPc = false;
    focusedAddress = address;
    manualBase = deriveDisassemblyBase(address, emulator.getMemorySize());
    elements.followPcToggle.checked = false;
  };

  const currentMode = (): DebugRuleAction => {
    if (elements.ruleModeTrace.checked) {
      return "trace";
    }
    if (elements.ruleModeTraceStop.checked) {
      return "trace_stop";
    }
    return "break";
  };

  const currentRuleSpec = (): DebuggerRuleSpec => {
    const action = currentMode();
    const spec: DebuggerRuleSpec = {
      action,
      everyNth: parsePositiveField(elements.traceEveryNth.value, "Every Nth hit"),
      logText: null,
      matchText: elements.breakpointAddress.value,
      maxEvents: parsePositiveField(elements.traceMaxEvents.value, "Max events"),
      name: elements.ruleName.value.trim() === "" ? null : elements.ruleName.value.trim()
    };
    if (action !== "break") {
      spec.logText = elements.traceFields.value;
    }
    return spec;
  };

  const renderRuntime = (): void => {
    const snapshot = lastSnapshot;
    const midInstruction = snapshot !== null && !snapshot.halted && !snapshot.instrBoundary;
    const stateText =
      runtime.state === "running"
        ? "Debugger running"
        : runtime.state === "halted"
          ? "Debugger halted"
          : midInstruction
            ? "Debugger paused (mid instruction)"
            : "Debugger paused";
    elements.runtimeState.textContent = `${stateText}\nCycles: ${runtime.cycleCount} | Frame: ${runtime.frameNo}`;
    if (lastNotice !== null) {
      elements.stopReason.textContent = lastNotice;
      return;
    }
    elements.stopReason.textContent = stopReasonText(runtime.stopReason, snapshot, lastRule);
  };

  const renderRuleFormState = (): void => {
    const traceMode = currentMode() !== "break";
    elements.traceFieldsRow.hidden = !traceMode;
    elements.traceLimitsRow.hidden = !traceMode;
    elements.ruleNameRow.hidden = !traceMode;
    elements.traceFields.disabled = !traceMode;
    elements.traceEveryNth.disabled = !traceMode;
    elements.traceMaxEvents.disabled = !traceMode;
    elements.ruleName.disabled = !traceMode;
  };

  const renderPatchFormState = (): void => {
    const mode = currentPatchMode();

    switch (mode) {
      case "bytes":
        elements.patchValueLabel.textContent = "Bytes (hex)";
        elements.patchValue.placeholder = "EA EA";
        break;
      case "fill":
        elements.patchValueLabel.textContent = "Fill byte (hex)";
        elements.patchValue.placeholder = "EA";
        break;
    }
    renderPatchWarning();
    renderPatchPreview();
  };

  const renderControls = (): void => {
    const halted = lastSnapshot?.halted ?? false;
    const actionReady = actions !== null;
    const showPause = actionReady && runtime.state === "running";
    const showContinue = actionReady && !halted && runtime.state !== "running";

    elements.pauseTrigger.hidden = !showPause;
    elements.continueTrigger.hidden = !showContinue;
    elements.pauseTrigger.disabled = !showPause;
    elements.continueTrigger.disabled = !showContinue;
    elements.stepTrigger.disabled = !actionReady || halted || runtime.state === "running";
    elements.step10Trigger.disabled = !actionReady || halted || runtime.state === "running";
    elements.breakpointAddress.disabled = !actionReady;
    elements.ruleModeBreak.disabled = !actionReady;
    elements.ruleModeTrace.disabled = !actionReady;
    elements.ruleModeTraceStop.disabled = !actionReady;
    elements.addBreakpointTrigger.disabled = !actionReady;
    elements.showBreakpointTrigger.disabled = lastSnapshot === null;
    elements.clearBreakpointsTrigger.disabled = !actionReady;
    elements.patchAddress.disabled = !actionReady;
    elements.patchReadLength.disabled = !actionReady;
    elements.patchModeBytes.disabled = !actionReady;
    elements.patchModeFill.disabled = !actionReady;
    elements.patchValue.disabled = !actionReady;
    elements.patchCount.disabled = !actionReady || currentPatchMode() !== "fill";
    elements.patchReadTrigger.disabled = !actionReady;
    elements.patchApplyTrigger.disabled = !actionReady || runtime.state === "running";
    elements.traceClearTrigger.disabled = lastTraceEntries.length === 0;
    elements.traceExportTrigger.disabled = lastTraceEntries.length === 0;
    elements.traceToggleTrigger.disabled = !actionReady;
    renderRuleFormState();
    renderPatchFormState();
    if (!actionReady) {
      elements.ruleName.disabled = true;
      elements.traceFields.disabled = true;
      elements.traceEveryNth.disabled = true;
      elements.traceMaxEvents.disabled = true;
      elements.patchReadLength.disabled = true;
      elements.patchValue.disabled = true;
      elements.patchCount.disabled = true;
    }
    elements.patchCountRow.setAttribute("aria-disabled", elements.patchCount.disabled ? "true" : "false");
  };

  const renderRules = (rules: DebuggerRuleInfo[]): void => {
    elements.breakpointsOutput.textContent = "";
    if (rules.length === 0) {
      elements.breakpointsOutput.textContent = "No rules set.";
      return;
    }

    const fragment = document.createDocumentFragment();
    rules.forEach((rule, index) => {
      const row = document.createElement("article");
      row.className = "debugger-breakpoints__row";

      const summary = document.createElement("div");
      summary.className = "debugger-breakpoints__summary";

      const meta = document.createElement("div");
      meta.className = "debugger-breakpoints__meta";

      const badge = document.createElement("span");
      badge.className = `debugger-rule-badge debugger-rule-badge--${rule.action.replace("_", "-")}`;
      badge.textContent = ruleBadgeText(rule.action);

      const name = document.createElement("strong");
      name.className = "debugger-breakpoints__name";
      name.textContent = rule.name;

      const hits = document.createElement("span");
      hits.className = "debugger-breakpoints__stats";
      hits.textContent =
        rule.action === "break"
          ? `hits: ${rule.hitCount}`
          : `hits: ${rule.hitCount} | logged: ${rule.traceCount}/${rule.maxEvents}`;

      meta.append(badge, name, hits);

      const match = document.createElement("p");
      match.className = "debugger-breakpoints__expr";
      match.textContent = `match: ${rule.matchText}`;

      summary.append(meta, match);

      if (rule.action !== "break") {
        const traceMeta = document.createElement("p");
        traceMeta.className = "debugger-breakpoints__trace";
        traceMeta.textContent = `log: ${rule.logText} | every ${rule.everyNth} hit(s) | max ${rule.maxEvents}`;
        summary.append(traceMeta);
      }

      const actionsRow = document.createElement("div");
      actionsRow.className = "debugger-breakpoints__actions";

      if (rule.anchorAddress !== null) {
        const jump = document.createElement("button");
        jump.type = "button";
        jump.className = "debugger-breakpoints__jump";
        jump.textContent = `0x${formatHexWord(rule.anchorAddress)}`;
        jump.addEventListener("click", () => {
          focusAddress(rule.anchorAddress ?? 0);
          void refresh();
        });
        actionsRow.append(jump);
      }

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "debugger-breakpoints__delete";
      remove.textContent = "Delete";
      remove.disabled = actions === null;
      remove.addEventListener("click", () => {
        try {
          emulator.removeDebuggerRule(index);
          actions?.activate();
          lastNotice = `Removed ${ruleBadgeText(rule.action)} rule: ${rule.name}.`;
          if (focusedAddress === rule.anchorAddress) {
            focusedAddress = null;
          }
          void refresh();
        } catch (error) {
          lastNotice = error instanceof Error ? error.message : String(error);
          renderRuntime();
        }
      });

      actionsRow.append(remove);
      row.append(summary, actionsRow);
      fragment.append(row);
    });
    elements.breakpointsOutput.append(fragment);
  };

  const renderTraceLog = (entries: TraceLogEntry[]): void => {
    const shouldAutoscroll = elements.traceAutoscrollToggle.checked && entries.length > lastTraceEntries.length;
    lastTraceEntries = entries;
    elements.traceOutput.textContent = "";
    if (entries.length === 0) {
      elements.traceOutput.textContent = "No trace events recorded.";
      return;
    }

    const table = document.createElement("table");
    table.className = "debugger-trace-table";

    const header = document.createElement("thead");
    header.innerHTML = "<tr><th>#</th><th>Cycle</th><th>Frame</th><th>PC</th><th>Rule</th><th>Values</th></tr>";
    table.append(header);

    const body = document.createElement("tbody");
    for (const entry of entries) {
      const row = document.createElement("tr");
      row.className = "debugger-trace-table__row";
      row.tabIndex = 0;
      for (const value of [
        entry.sequenceNo,
        entry.cycleCount,
        entry.frameNo.toString(),
        `0x${formatHexWord(entry.pc)}`,
        entry.ruleName,
        entry.values
      ]) {
        const cell = document.createElement("td");
        cell.textContent = value;
        row.append(cell);
      }
      const jump = () => {
        focusAddress(entry.pc);
        lastNotice = `Showing trace event ${entry.sequenceNo} at 0x${formatHexWord(entry.pc)}.`;
        void refresh();
      };
      row.addEventListener("click", jump);
      row.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          jump();
        }
      });
      body.append(row);
    }
    table.append(body);
    elements.traceOutput.append(table);
    if (shouldAutoscroll) {
      elements.traceOutput.scrollTop = elements.traceOutput.scrollHeight;
    }
  };

  const renderTraceControls = (): void => {
    elements.traceToggleTrigger.textContent = emulator.isTraceEnabled() ? "Pause Trace" : "Resume Trace";
  };

  const refreshRuleSurfaces = (): DebuggerRuleInfo[] => {
    const rules = emulator.listDebuggerRules();
    renderRules(rules);
    renderTraceLog(emulator.listTraceLogEntries());
    renderTraceControls();
    return rules;
  };

  const renderDisassembly = (snapshot: CoreSnapshot, rules: DebuggerRuleInfo[]): void => {
    renderDisassemblyWindow(
      elements.disassemblyOutput,
      emulator,
      snapshot,
      rules.map(asBreakpointInfo),
      followPc,
      manualBase,
      focusedAddress
    );
  };

  const refresh = (): void => {
    try {
      const snapshot = emulator.getState();
      const machineSnapshots: MachineSnapshots = {
        ioc: emulator.getIocState(),
        voc: emulator.getVocState(),
        aoc: emulator.getAocState(),
        tic: emulator.getTicState(),
        dic: emulator.getDicState()
      };
      const rules = refreshRuleSurfaces();
      lastRule = emulator.getLastDebuggerRule();
      lastSnapshot = snapshot;
      if (patchPreviewAddress !== null && runtime.state !== "running") {
        refreshPatchPreview();
      } else {
        renderPatchPreview();
      }
      renderSnapshot(elements, snapshot);
      renderMachineState(elements, machineSnapshots);
      renderRuntime();
      renderControls();
      renderDisassembly(snapshot, rules);
      syncDesktopCardHeights();
    } catch (error) {
      lastRule = null;
      lastNotice = error instanceof Error ? error.message : String(error);
      renderPatchPreview();
      renderRuntime();
      renderControls();
      clearDisassembly(elements.disassemblyOutput, error instanceof Error ? error.message : String(error));
      syncDesktopCardHeights();
    }
  };

  const updateRunningTraceSurface = (): void => {
    try {
      refreshRuleSurfaces();
      renderControls();
    } catch {
      // Keep runtime updates resilient while the emulator is running.
    }
  };

  const runAction = (fn: () => void): void => {
    try {
      fn();
      lastNotice = null;
      renderControls();
      renderRuntime();
    } catch (error) {
      lastNotice = error instanceof Error ? error.message : String(error);
      renderRuntime();
    }
  };

  elements.pauseTrigger.addEventListener("click", () => {
    const activeActions = actions;
    if (activeActions === null) {
      return;
    }
    runAction(() => {
      activeActions.pause();
    });
  });

  elements.continueTrigger.addEventListener("click", () => {
    const activeActions = actions;
    if (activeActions === null) {
      return;
    }
    runAction(() => {
      activeActions.continue();
    });
  });

  elements.stepTrigger.addEventListener("click", () => {
    const activeActions = actions;
    if (activeActions === null) {
      return;
    }
    runAction(() => {
      activeActions.step(1);
      refresh();
    });
  });

  elements.step10Trigger.addEventListener("click", () => {
    const activeActions = actions;
    if (activeActions === null) {
      return;
    }
    runAction(() => {
      activeActions.step(10);
      refresh();
    });
  });

  elements.iocClearTrigger.addEventListener("click", () => {
    try {
      emulator.clearIocLatches();
      lastNotice = "Cleared IOC sticky activity.";
      refresh();
    } catch (error) {
      lastNotice = error instanceof Error ? error.message : String(error);
      renderRuntime();
    }
  });

  elements.refreshTrigger.addEventListener("click", () => {
    lastNotice = runtime.state === "running" ? "Snapshot refreshed while running." : null;
    refresh();
  });

  for (const input of [elements.ruleModeBreak, elements.ruleModeTrace, elements.ruleModeTraceStop]) {
    input.addEventListener("change", () => {
      renderRuleFormState();
      renderControls();
    });
  }

  for (const input of [elements.patchModeBytes, elements.patchModeFill]) {
    input.addEventListener("change", () => {
      renderControls();
    });
  }

  for (const input of [elements.patchAddress, elements.patchValue, elements.patchCount]) {
    input.addEventListener("input", () => {
      renderPatchWarning();
    });
  }

  elements.patchReadTrigger.addEventListener("click", () => {
    try {
      patchPreviewAddress = parsePatchAddress();
      patchPreviewLength = parsePatchReadLength(patchPreviewAddress);
      refreshPatchPreview();
      patchStatusText = `Read ${patchPreviewLength} byte${patchPreviewLength === 1 ? "" : "s"} from 0x${formatHexWord(
        patchPreviewAddress
      )}.`;
      renderPatchPreview();
      renderPatchWarning();
    } catch (error) {
      patchStatusText = error instanceof Error ? error.message : String(error);
      renderPatchPreview();
      renderPatchWarning();
    }
  });

  elements.patchApplyTrigger.addEventListener("click", () => {
    try {
      const mode = currentPatchMode();
      const address = parsePatchAddress();
      const payload = buildPatchPayload();

      if (runtime.state === "running") {
        throw new Error("Pause the debugger before applying memory patches.");
      }
      emulator.writeMemory(address, payload);
      patchPreviewAddress = address;
      patchPreviewLength = patchReadLengthOrDefault(address);
      patchStatusText =
        mode === "fill"
          ? `Filled ${payload.length} byte${payload.length === 1 ? "" : "s"} at 0x${formatHexWord(address)}.`
          : `Wrote ${payload.length} byte${payload.length === 1 ? "" : "s"} to 0x${formatHexWord(address)}.`;
      refreshPatchPreview();
      refresh();
    } catch (error) {
      patchStatusText = error instanceof Error ? error.message : String(error);
      renderPatchPreview();
      renderPatchWarning();
    }
  });

  elements.addBreakpointTrigger.addEventListener("click", () => {
    if (actions === null) {
      return;
    }
    try {
      const spec = currentRuleSpec();
      const rule =
        spec.action === "break"
          ? (() => {
              const breakpoint = emulator.describeBreakpointExpression(spec.matchText);
              return {
                action: "break" as const,
                anchorAddress: breakpoint.anchorAddress,
                everyNth: 1,
                hitCount: 0,
                logText: "",
                matchText: breakpoint.text,
                maxEvents: 0,
                name: breakpoint.text,
                traceCount: 0
              };
            })()
          : emulator.describeDebuggerRule(spec);
      try {
        emulator.addDebuggerRule(spec);
      } catch (error) {
        if (spec.action === "break") {
          throw new Error("failed to add breakpoint condition");
        }
        throw error;
      }
      actions.activate();
      lastNotice = ruleNoticeText(rule);
      refresh();
    } catch (error) {
      lastNotice = error instanceof Error ? error.message : String(error);
      renderRuntime();
    }
  });

  elements.showBreakpointTrigger.addEventListener("click", () => {
    try {
      const breakpoint = emulator.describeBreakpointExpression(elements.breakpointAddress.value);
      if (breakpoint.anchorAddress === null) {
        throw new Error("show requires a PC address anchor");
      }
      focusAddress(breakpoint.anchorAddress);
      lastNotice = `Showing code around 0x${formatHexWord(breakpoint.anchorAddress)}.`;
      refresh();
    } catch (error) {
      lastNotice = error instanceof Error ? error.message : String(error);
      renderRuntime();
    }
  });

  elements.clearBreakpointsTrigger.addEventListener("click", () => {
    if (actions === null) {
      return;
    }
    try {
      emulator.clearDebuggerRules();
      actions.activate();
      focusedAddress = null;
      lastNotice = "Cleared all debugger rules.";
      refresh();
    } catch (error) {
      lastNotice = error instanceof Error ? error.message : String(error);
      renderRuntime();
    }
  });

  elements.traceClearTrigger.addEventListener("click", () => {
    try {
      emulator.clearTraceLog();
      lastNotice = "Cleared trace log.";
      refresh();
    } catch (error) {
      lastNotice = error instanceof Error ? error.message : String(error);
      renderRuntime();
    }
  });

  elements.traceToggleTrigger.addEventListener("click", () => {
    try {
      const enabled = emulator.isTraceEnabled();
      emulator.setTraceEnabled(!enabled);
      lastNotice = enabled ? "Trace capture paused." : "Trace capture resumed.";
      refresh();
    } catch (error) {
      lastNotice = error instanceof Error ? error.message : String(error);
      renderRuntime();
    }
  });

  elements.traceExportTrigger.addEventListener("click", () => {
    if (lastTraceEntries.length === 0) {
      return;
    }

    const lines = ["sequence,cycle,frame,pc,rule,values"];
    for (const entry of lastTraceEntries) {
      const escapeCsv = (value: string) => `"${value.split('"').join('""')}"`;
      lines.push(
        [
          entry.sequenceNo,
          entry.cycleCount,
          entry.frameNo.toString(),
          `0x${formatHexWord(entry.pc)}`,
          escapeCsv(entry.ruleName),
          escapeCsv(entry.values)
        ].join(",")
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "am8-trace-log.csv";
    anchor.click();
    URL.revokeObjectURL(url);
    lastNotice = `Exported ${lastTraceEntries.length} trace event${lastTraceEntries.length === 1 ? "" : "s"}.`;
    renderRuntime();
  });

  elements.followPcToggle.addEventListener("change", () => {
    followPc = elements.followPcToggle.checked;
    if (followPc) {
      focusedAddress = null;
      manualBase = null;
    } else if (focusedAddress === null && lastSnapshot !== null) {
      focusedAddress = lastSnapshot.pc;
      manualBase = deriveDisassemblyBase(focusedAddress, emulator.getMemorySize());
    }
    refresh();
  });

  for (const tab of machineTabs) {
    tab.button.addEventListener("click", () => {
      activeMachineTab = tab.id;
      setMachineStateTabState(activeMachineTab, machineTabs);
    });
  }

  elements.followPcToggle.checked = true;
  elements.ruleModeBreak.checked = true;
  renderRuleFormState();
  setMachineStateTabState(activeMachineTab, machineTabs);
  renderRuntime();
  renderControls();
  clearDisassembly(elements.disassemblyOutput, "Refresh to inspect the current code window.");
  elements.breakpointsOutput.textContent = "No rules set.";
  elements.traceOutput.textContent = "No trace events recorded.";
  window.addEventListener("resize", syncDesktopCardHeights);
  refresh();

  return {
    refresh,
    setActions(nextActions) {
      actions = nextActions;
      renderControls();
    },
    updateRuntime(nextRuntime) {
      runtime = nextRuntime;
      if (runtime.stopReason !== "none" || runtime.state === "running") {
        lastNotice = null;
      }
      renderRuntime();
      renderControls();
      if (runtime.state === "running") {
        updateRunningTraceSurface();
      }
    }
  };
}
